// Waiting-room host approval gate (docs/waiting-room-approval.md).
//
// A guest arriving on an invite link asks the host for entry after the device
// check and holds here until someone with the `moderate` capability admits or
// denies them. The guest has deliberately NOT joined Photon yet, so there is
// no push channel: both sides poll HTTP.
//
// Fail-open policy: infra/auth errors (network, 5xx, a stale-token 401) admit
// the guest anyway - a broken lobby must never strand everyone. The
// joinSession backstop (src/routes/moderation.js) stays authoritative.
// Pending/denied are 200 responses and never fail open.
import { apiService } from '../services/api';
import { analytics } from '../services/analytics';
import { sessionStore } from '../state/sessionStore';

const ENTRY_POLL_MS = 3_000;

/**
 * Ask the host for entry and resolve once the answer is in.
 *
 * @param {Object} [opts]
 * @param {() => void} [opts.onWaiting] fired once if the guest actually has to
 *   wait (skipped on the immediate-admit paths, so the UI never flashes)
 * @param {number} [opts.pollMs] status-poll period; only tests pass this, so
 *   they can run on real timers (fake timers leak across this repo's shared
 *   test worker - see the zombie-handler note in tests/setup.js)
 * @returns {{ promise: Promise<'admitted'|'denied'|'canceled'>, cancel: () => void }}
 */
export function requestEntryApproval({ onWaiting, pollMs = ENTRY_POLL_MS } = {}) {
  let entryRequestId = null;
  let pollTimer = null;
  let reRequested = false; // one re-request after a lost row, then fail open
  let settled = false;
  /** @type {(status: 'admitted'|'denied'|'canceled') => void} */
  let resolveOuter;
  const promise = new Promise((resolve) => {
    resolveOuter = resolve;
  });

  function settle(status) {
    if (settled) return;
    settled = true;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    window.removeEventListener('pagehide', sendCancelBeacon);
    resolveOuter(status);
  }

  // Best-effort "I stopped waiting" on tab close, so the host's list drops
  // this guest promptly instead of waiting out the liveness window. Same
  // beacon pattern as sendLeaveBeacon in src/multiplayer/participantRegistry.ts
  // (the token rides the body - sendBeacon can't set an Authorization header).
  function sendCancelBeacon() {
    const sessionToken = localStorage.getItem('oggi_session');
    if (!sessionToken || !entryRequestId || !navigator.sendBeacon) return;
    try {
      const blob = new Blob([JSON.stringify({ sessionToken, entryRequestId })], {
        type: 'application/json',
      });
      navigator.sendBeacon('/api/video-sessions/cancel-entry', blob);
    } catch {
      /* best-effort */
    }
  }

  async function poll() {
    if (settled || !entryRequestId) return;
    let result;
    try {
      result = await apiService.getEntryStatus(entryRequestId);
    } catch (err) {
      if (err && err.status === 404) {
        // Row lost (server restart, ended session): re-issue once, then fail
        // open.
        if (!reRequested) {
          reRequested = true;
          try {
            const again = await apiService.requestEntry(sessionStore.getState().playerName);
            if (settled) return;
            if (again.status === 'pending' && again.entryRequestId) {
              entryRequestId = again.entryRequestId;
              return;
            }
          } catch {
            /* fall through to fail-open */
          }
        }
        console.warn('[EntryApproval] Entry request lost - entering (fail-open)');
        analytics.track('lobby_admitted');
        settle('admitted');
      }
      return; // transient error - keep polling
    }
    if (settled) return;
    if (result.status === 'admitted' || result.waitingRoomEnabled === false) {
      // multiplayer.js passes this to POST /api/video-sessions/join.
      window.OGGI_ENTRY_REQUEST_ID = entryRequestId;
      analytics.track('lobby_admitted');
      settle('admitted');
    } else if (result.status === 'denied') {
      analytics.track('lobby_denied');
      settle('denied');
    } else if (result.status === 'canceled') {
      settle('canceled'); // canceled from another tab
    }
  }

  async function start() {
    // Only invite sessions are gated: without an oggi_session token (open
    // access, dev) entry is immediate, exactly as before the lobby existed.
    if (!localStorage.getItem('oggi_session')) {
      settle('admitted');
      return;
    }
    let entry;
    try {
      entry = await apiService.requestEntry(sessionStore.getState().playerName);
    } catch (err) {
      console.warn('[EntryApproval] request-entry failed - entering (fail-open):', err);
      settle('admitted');
      return;
    }
    if (settled) return;

    if (entry.status === 'pending' && entry.entryRequestId) {
      entryRequestId = entry.entryRequestId;
      analytics.track('lobby_wait');
      if (onWaiting) onWaiting();
      window.addEventListener('pagehide', sendCancelBeacon);
      pollTimer = setInterval(poll, pollMs);
    } else {
      // Admitted immediately: bypass role, open access, or lobby disabled.
      settle('admitted');
    }
  }

  start();

  return {
    promise,
    cancel() {
      if (entryRequestId) apiService.cancelEntry(entryRequestId); // fire-and-forget
      settle('canceled');
    },
  };
}
